import { defineMessages, useIntl, IntlShape } from 'react-intl'
import { Button, Text, Box, Stack } from '@homebotapp/hb-react-component-catalog'
import { useVerificationFlow } from '../../VerificationFlowContext'
import { formatStreetAddress } from '../../../../../helpers/formatListingAddress'
import { formatCustomerProfileType } from '../../../../../helpers/formatCustomerProfileType'
import { selectAddress } from '../../../../../store/selectors/home'
import { selectFullName } from '../../../../../store/selectors/customerProfile'
import { useSelector } from 'react-redux'
import { PreferredPartnerCard } from '../../../HomebotNetwork/PreferredPartnerCard'
import { ClientTeamMembership } from '../../../../../types/clientTeamMembership'
import { ListingAddress } from '../../../../../api/gqlaxy/gql/generated/graphql'
import { useUnverifiedStep } from '../../../../../hooks/homebotNetwork/verificationFlow/useUnverifiedStep'

const MSG = defineMessages({
  add: {
    id: 'VerificationFlow.UnverifiedOptions.add',
    defaultMessage: 'Yes, add'
  },
  error: {
    id: 'VerificationFlow.UnverifiedOptions.error',
    defaultMessage: 'An error occurred while verifying the partner.'
  },
  heading: {
    id: 'VerificationFlow.UnverifiedOptions.heading',
    defaultMessage: 'We found your {customerType} from {streetAddress}. Would you like to add them to the conversation?'
  },
  headingIa: {
    id: 'VerificationFlow.UnverifiedOptions.headingIa',
    defaultMessage:
      '{customerFullName} added an insurance agent to your Homebot. Would you like to add them to the conversation?'
  },
  remove: {
    id: 'VerificationFlow.UnverifiedOptions.remove',
    defaultMessage: 'No, remove'
  },
  subTextRea: {
    id: 'VerificationFlow.UnverifiedOptions.subTextRea',
    defaultMessage:
      "If so, we'll keep them on your team, and they'll be kept in the loop with real estate related questions."
  },
  subTextIa: {
    id: 'VerificationFlow.UnverifiedOptions.subTextIa',
    defaultMessage:
      "If so, we'll keep them on your team, and they'll be kept in the loop with insurance-related questions."
  },
  subTextLo: {
    id: 'VerificationFlow.UnverifiedOptions.subTextLo',
    defaultMessage: "We'll keep them on your team to help with finance questions."
  },
  terms: {
    id: 'VerificationFlow.UnverifiedOptions.terms',
    defaultMessage:
      "By clicking 'Yes, Add' you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your contact information (name and email address) and any other information you include in your question with the agent identified above. You agree that the agent may contact you about your inquiry."
  }
})

/**
 * UnverifiedRealEstateAgentView Component
 *
 * Specific UI for unverified real estate agents
 */
const UnverifiedRealEstateAgentView = ({
  membership,
  streetAddress,
  isProcessing,
  onVerify,
  onRemove,
  intl
}: {
  membership: ClientTeamMembership
  streetAddress: ListingAddress
  isProcessing: boolean
  onVerify: () => void
  onRemove: () => void
  intl: IntlShape
}) => (
  <Box>
    <Box data-qa='unverified-view' data-testid='unverified-view'>
      <PreferredPartnerCard partner={membership.customerProfile} corporateProfile={membership.corporateProfile} />
      <Text fontWeight='semibold' color='neutral.900' mt={8}>
        {intl.formatMessage(MSG.heading, {
          streetAddress: formatStreetAddress(streetAddress),
          customerType: formatCustomerProfileType('RealEstateAgent')
        })}
      </Text>
      <Text size='sm' color='neutral.800' mb='7'>
        {intl.formatMessage(MSG.subTextRea)}
      </Text>

      <Stack direction='row' gap={2} justifyContent='flex-end' mt='6'>
        <Button
          variant='ghost'
          colorScheme='danger'
          onClick={onRemove}
          isDisabled={isProcessing}
          tracking={{
            guid: 'rPr4FjtTR38XuUF7z8uzqH',
            ui_context: 'verification_flow_unverified_partner_view',
            descriptor: MSG.remove.defaultMessage
          }}
        >
          {intl.formatMessage(MSG.remove)}
        </Button>
        <Button
          isLoading={isProcessing}
          onClick={onVerify}
          colorScheme='primary'
          tracking={{
            guid: 'swVcbjtxnb4XTqjLCaesBg',
            ui_context: 'verification_flow_unverified_partner_view',
            descriptor: MSG.add.defaultMessage
          }}
        >
          {intl.formatMessage(MSG.add)}
        </Button>
      </Stack>
    </Box>
    <Box bg='neutral.200' borderTop='1px solid' borderColor='neutral.300' mx='-6' mt='6' mb='-6' p='6'>
      <Text size='3xs' color='neutral.600'>
        {intl.formatMessage(MSG.terms)}
      </Text>
    </Box>
  </Box>
)

/**
 * UnverifiedLoanOfficerView Component
 *
 * Specific UI for unverified loan officers (simple add/remove flow)
 */
const UnverifiedLoanOfficerView = ({
  membership,
  streetAddress,
  isProcessing,
  onVerify,
  onRemove,
  intl
}: {
  membership: ClientTeamMembership
  streetAddress: ListingAddress
  isProcessing: boolean
  onVerify: () => void
  onRemove: () => void
  intl: IntlShape
}) => (
  <Box>
    <Box data-qa='unverified-view' data-testid='unverified-view'>
      <PreferredPartnerCard partner={membership.customerProfile} corporateProfile={membership.corporateProfile} />
      <Text fontWeight='semibold' color='neutral.900' mt={8}>
        {intl.formatMessage(MSG.heading, {
          streetAddress: formatStreetAddress(streetAddress),
          customerType: formatCustomerProfileType('LoanOfficer')
        })}
      </Text>
      <Text size='sm' color='neutral.800' mb='7'>
        {intl.formatMessage(MSG.subTextLo)}
      </Text>

      <Stack direction='row' gap={2} justifyContent='flex-end' mt='6'>
        <Button variant='ghost' colorScheme='danger' onClick={onRemove} isDisabled={isProcessing}>
          {intl.formatMessage(MSG.remove)}
        </Button>
        <Button isLoading={isProcessing} onClick={onVerify} colorScheme='primary'>
          {intl.formatMessage(MSG.add)}
        </Button>
      </Stack>
    </Box>
    <Box bg='neutral.200' borderTop='1px solid' borderColor='neutral.300' mx='-6' mt='6' mb='-6' p='6'>
      <Text size='3xs' color='neutral.600'>
        {intl.formatMessage(MSG.terms)}
      </Text>
    </Box>
  </Box>
)

/**
 * UnverifiedInsuranceAgentView Component
 *
 * Specific UI for unverified insurance agents
 */
const UnverifiedInsuranceAgentView = ({
  membership,
  streetAddress,
  isProcessing,
  onVerify,
  onRemove,
  intl
}: {
  membership: ClientTeamMembership
  streetAddress: ListingAddress
  isProcessing: boolean
  onVerify: () => void
  onRemove: () => void
  intl: IntlShape
}) => {
  const customerFullName = useSelector(selectFullName)
  return (
    <Box>
      <Box data-qa='unverified-view' data-testid='unverified-view'>
        <PreferredPartnerCard partner={membership.customerProfile} corporateProfile={membership.corporateProfile} />
        <Text fontWeight='semibold' color='neutral.900' mt={8}>
          {intl.formatMessage(MSG.headingIa, {
            customerFullName: customerFullName
          })}
        </Text>
        <Text size='sm' color='neutral.800' mb='7'>
          {intl.formatMessage(MSG.subTextIa)}
        </Text>

        <Stack direction='row' gap={2} justifyContent='flex-end' mt='6'>
          <Button
            variant='ghost'
            colorScheme='danger'
            onClick={onRemove}
            isDisabled={isProcessing}
            tracking={{
              guid: '2aP4URBdMFKYiwkgMXgoJm',
              ui_context: 'verification_flow_unverified_partner_view',
              descriptor: MSG.remove.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.remove)}
          </Button>
          <Button
            isLoading={isProcessing}
            onClick={onVerify}
            colorScheme='primary'
            tracking={{
              guid: 'swVcbjtxnb4XTqjLCaesBg',
              ui_context: 'verification_flow_unverified_partner_view',
              descriptor: MSG.add.defaultMessage
            }}
          >
            {intl.formatMessage(MSG.add)}
          </Button>
        </Stack>
      </Box>
      <Box bg='neutral.200' borderTop='1px solid' borderColor='neutral.300' mx='-6' mt='6' mb='-6' p='6'>
        <Text size='3xs' color='neutral.600'>
          {intl.formatMessage(MSG.terms)}
        </Text>
      </Box>
    </Box>
  )
}

/**
 * UnverifiedView Component
 *
 * Generic component for showing unverified partner options (both RE agents and LOs).
 * Handles all the logic and renders the appropriate subcomponent.
 */
const UnverifiedView = () => {
  const intl = useIntl()
  const {
    state: { currentPartnerType }
  } = useVerificationFlow()
  const streetAddress = useSelector(selectAddress)
  const { membership, isLoading, onRemove, onVerify } = useUnverifiedStep()

  if (!membership) {
    return null // No membership to verify
  }

  // Render the appropriate subcomponent based on partner type
  const getComponent = () => {
    switch (currentPartnerType) {
      case 'LoanOfficer':
        return UnverifiedLoanOfficerView
      case 'RealEstateAgent':
        return UnverifiedRealEstateAgentView
      case 'InsuranceAgent':
        return UnverifiedInsuranceAgentView
      default:
        return UnverifiedRealEstateAgentView
    }
  }
  const Component = getComponent()

  return (
    <Component
      membership={membership}
      streetAddress={streetAddress}
      isProcessing={isLoading}
      onVerify={onVerify}
      onRemove={onRemove}
      intl={intl}
    />
  )
}

export default UnverifiedView
